import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createAgenticPrompt, updateAgenticPrompt } from 'services/agenticPrompts'
import { useNotification } from 'components/Notification'

import { AGENTIC_PROMPTS_QUERY_KEY } from './useAgenticPrompts'
import { AGENTIC_PROMPT_QUERY_KEY } from './useAgenticPrompt'

/**
 * Mutation hook for creating or updating an agentic prompt. Determines which
 * action to take from the presence of an `id` on the submitted form values.
 * @param {object} options - hook options
 * @param {function} options.onSuccess - called after successful save with the saved prompt
 * @returns {object} - { savePrompt, isSaving }
 */
function useUpsertAgenticPrompt({ onSuccess } = {}) {
  const queryClient = useQueryClient()
  const { createNotification } = useNotification()

  const { mutate: savePrompt, isLoading: isSaving } = useMutation({
    mutationFn: ({ id, ...values }) =>
      id ? updateAgenticPrompt(id, values) : createAgenticPrompt(values),
    onSuccess: prompt => {
      createNotification({
        message: `Prompt "${prompt.title}" saved`,
        variant: 'success',
      })
      queryClient.invalidateQueries({ queryKey: [AGENTIC_PROMPTS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [AGENTIC_PROMPT_QUERY_KEY, prompt.id] })
      onSuccess?.(prompt)
    },
    onError: error => {
      const message =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.errors?.[0] ||
        error?.message ||
        'Failed to save prompt'
      createNotification({ message, variant: 'error' })
    },
  })

  return { savePrompt, isSaving }
}

export { useUpsertAgenticPrompt }
